// NodeMissionTaskHeader.jsx

import React, { useCallback } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  getMissionBotJobLabel,
  MISSION_TASK_HANDLED_BY,
} from 'missions/taskmodal/missionTaskModalUtils';

// CSS
import './NodeMissionTaskHeader.css';

// Icons
import Icon from 'svg/Icon';

// Components
import NodeMissionTaskCountdown from 'node/missions/mission/task/countdown/NodeMissionTaskCountdown';

/**
 * NodeMissionTaskHeader
 *
 * Displays the header for a Mission task.
 *
 * @param {object} task - The Mission task to display.
 * @param {object} taskStatusPresentation - The presentation for the task status.
 * @param {object|null} missionRun - Parent workflow run (for countdown deadline).
 * @param {boolean} [compact=false] - Peek density mode.
 * @param {boolean} [bodyExpanded=true] - Whether the task body is expanded.
 * @param {function|null} [onToggleBodyExpanded] - Optional expand/collapse handler.
 * @param {string} [percentLabel=''] - Task Checklist percent, e.g. "33%".
 *
 * @returns {ReactNode} - NodeMissionTaskHeader.
 */
const NodeMissionTaskHeader = React.memo(({
  task,
  taskStatusPresentation,
  missionRun = null,
  compact = false,
  bodyExpanded = true,
  onToggleBodyExpanded = null,
  percentLabel = '',
}) => {
  const { theme } = useTheme();

  const isPeopleTask = task?.handledBy !== MISSION_TASK_HANDLED_BY.BOT;
  const canToggle = typeof onToggleBodyExpanded === 'function';
  const showDetails = bodyExpanded !== false;

  const handleActivate = useCallback(() => {
    if (!canToggle) return;
    onToggleBodyExpanded();
  }, [canToggle, onToggleBodyExpanded]);

  const handleKeyDown = useCallback((event) => {
    if (!canToggle) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onToggleBodyExpanded();
  }, [canToggle, onToggleBodyExpanded]);

  return (
    <div
      className={[
        'node-mission-task-header-container',
        canToggle ? 'is-toggleable' : '',
        compact && !showDetails ? 'is-collapsed' : '',
      ].filter(Boolean).join(' ')}
      role={canToggle ? 'button' : undefined}
      tabIndex={canToggle ? 0 : undefined}
      aria-expanded={canToggle ? showDetails : undefined}
      onClick={canToggle ? handleActivate : undefined}
      onKeyDown={canToggle ? handleKeyDown : undefined}
    >

      <div className="node-mission-task-header-top">
        <div className="node-mission-task-header-main">
          <div className="node-mission-task-header-title-row">
            {canToggle ? (
              <Icon
                iconKey={showDetails ? 'keyboard_arrow_down' : 'chevron_right'}
                width={22}
                height={22}
                color={theme.foregroundMuted}
              />
            ) : (
              <Icon
                iconKey={taskStatusPresentation.iconKey}
                width={22}
                height={22}
                color={theme.foregroundMuted}
              />
            )}

            <div className="node-mission-task-header-title">{task.title}</div>
            {percentLabel ? (
              <div className="node-mission-task-header-percent">
                {percentLabel}
              </div>
            ) : null}
          </div>

          {showDetails && String(task?.description || '').trim() ? (
            <div
              className="node-mission-task-header-subtitle"
              style={{ color: theme.foregroundMuted }}
            >
              {String(task.description).trim()}
            </div>
          ) : null}

          {showDetails && !isPeopleTask ? (
            <div
              className="node-mission-task-header-summary"
              style={{ color: theme.foregroundMuted }}
            >
              {['Bot', getMissionBotJobLabel(task?.missionBot?.job)]
                .filter(Boolean)
                .join(' • ')}
            </div>
          ) : null}
        </div>

        <div className="node-mission-task-header-status-group">
          {showDetails ? (
            <NodeMissionTaskCountdown
              missionRun={missionRun}
              task={task}
            />
          ) : null}

          <div
            className="node-mission-task-header-status-label"
            style={{
              backgroundColor: taskStatusPresentation.backgroundColor,
              color: taskStatusPresentation.color,
            }}
          >
            {taskStatusPresentation.label}
          </div>
        </div>
      </div>
    </div>
  );
});

NodeMissionTaskHeader.displayName = 'NodeMissionTaskHeader';

export default NodeMissionTaskHeader;
